import { z } from 'zod';

/**
 * PowerSchool · Timetable (middleware module `powerschool.timetable`). Shapes follow the
 * MAI-TeachPartner spec docs/Timetable.md (§2.3 vendor record, §4 model, §5.1 layout).
 */

export const POWERSCHOOL_TIMETABLE_WEEK_PATH = '/api/v1/powerschool/timetable/week';

/** School-week order (spec §3): a range runs forward through this order. */
export const TIMETABLE_DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;
export const timetableDaySchema = z.enum(TIMETABLE_DAYS);
export type TimetableDay = z.infer<typeof timetableDaySchema>;

/** Default bell list (spec §4.1) when the school has not set `periods`. */
export const DEFAULT_TIMETABLE_PERIODS: readonly string[] = [
  'HR',
  'P1',
  'P2',
  'P3',
  'P4',
  'P5',
  'P6',
];

const periodSchema = z.string().regex(/^[A-Za-z0-9]{1,10}$/);
const count = z.number().int().min(0);

/** `pastoral` = registration / advisory (spec §4.2); everything else is keyed by grade. */
export const timetableSectionCategorySchema = z.enum(['pastoral', 'grade']);
export type TimetableSectionCategory = z.infer<typeof timetableSectionCategorySchema>;

export const timetableSectionSchema = z.object({
  sectionId: z.string().min(1),
  sectionNumber: z.string(),
  courseNumber: z.string(),
  courseName: z.string(),
  /** course_name without its leading grade prefix: "08 Social Studies" → "Social Studies". */
  courseLabel: z.string(),
  gradeLevel: z.string(),
  room: z.string(),
  students: count,
  maxEnrollment: count,
  category: timetableSectionCategorySchema,
});
export type TimetableSection = z.infer<typeof timetableSectionSchema>;

export const timetableCellSchema = z.object({
  period: periodSchema,
  day: timetableDaySchema,
  /** Empty = "Free". */
  sections: z.array(timetableSectionSchema),
  /** Two or more sections share the slot (spec §4.6); never drop one. */
  clash: z.boolean(),
});
export type TimetableCell = z.infer<typeof timetableCellSchema>;

/** Spec §4.4: the HR row is excluded from the period counts. */
export const timetableStatsSchema = z.object({
  teachingPeriods: count,
  totalPeriods: count,
  freePeriods: count,
  /** Distinct section ids, all categories. */
  sections: count,
  /** Sum of students over non-pastoral sections. */
  studentsTaught: count,
});
export type TimetableStats = z.infer<typeof timetableStatsSchema>;

/** A section whose expression (or one of its tokens) did not parse (spec §3). */
export const timetableUnscheduledSchema = z.object({
  sectionNumber: z.string(),
  courseName: z.string(),
  expression: z.string(),
});
export type TimetableUnscheduled = z.infer<typeof timetableUnscheduledSchema>;

export const timetableSchoolSchema = z.object({
  schoolId: z.string().min(1),
  cells: z.array(timetableCellSchema),
  stats: timetableStatsSchema,
  unscheduled: z.array(timetableUnscheduledSchema),
});
export type TimetableSchool = z.infer<typeof timetableSchoolSchema>;

/**
 * GET /api/v1/powerschool/timetable/week. `no-teacher`: the signed-in token carries no email;
 * `no-sections`: PowerSchool returned no rows (no matching active teacher, or no sections in the
 * active terms; spec §2.2: the query can't tell them apart). Both come with `schools: []`.
 */
export const timetableWeekResponseSchema = z.object({
  status: z.enum(['ok', 'no-teacher', 'no-sections']),
  /** The school's IANA time zone (setting `schoolTimezone`). */
  timezone: z.string().min(1),
  /** Today in the school's time zone; null when today is outside the shown days. */
  today: timetableDaySchema.nullable(),
  /** Columns: Sun–Thu, plus Fri/Sat only if a section uses them (spec §4.1). */
  days: z.array(timetableDaySchema),
  /** Rows: the school's full bell list. */
  periods: z.array(periodSchema),
  /** One grid per PowerSchool school (spec §2.2). */
  schools: z.array(timetableSchoolSchema),
  fetchedAt: z.iso.datetime(),
  /** Set when the week is another person's (developer view-as, header X-TP-View-As). */
  viewingAs: z.email().optional(),
});
export type TimetableWeekResponse = z.infer<typeof timetableWeekResponseSchema>;

/** PowerQuery leaves out null columns, so a missing value reads as ''. */
const vendorString = z.string().default('');

/** One row of PowerQuery `ai.mantleai.teachpartner.teacher_sections` (spec §2.3): all strings.
 * Only section_id and schoolid are required; extra keys are ignored. */
export const powerSchoolSectionRecordSchema = z.object({
  teacher_id: vendorString,
  teacher_dcid: vendorString,
  teacher_email: vendorString,
  section_id: z.string().min(1),
  section_dcid: vendorString,
  schoolid: z.string().min(1),
  section_number: vendorString,
  external_expression: vendorString,
  grade_level: vendorString,
  no_of_students: vendorString,
  maxenrollment: vendorString,
  room: vendorString,
  course_number: vendorString,
  course_name: vendorString,
  term_id: vendorString,
  year_id: vendorString,
  term_name: vendorString,
  term_abbreviation: vendorString,
  term_portion: vendorString,
  term_isyearrec: vendorString,
  term_firstday: vendorString,
  term_lastday: vendorString,
});
export type PowerSchoolSectionRecord = z.infer<typeof powerSchoolSectionRecordSchema>;

/**
 * PowerQuery response. `count` is present with `count=true`; `record` may be absent when
 * nothing matched (treated as empty). Extra vendor keys such as `@extensions` are ignored.
 */
export const powerSchoolSectionsResponseSchema = z.object({
  count: z.coerce.number().int().min(0).optional(),
  record: z.array(powerSchoolSectionRecordSchema).default([]),
});
export type PowerSchoolSectionsResponse = z.infer<typeof powerSchoolSectionsResponseSchema>;
